import { Button } from "../ui/button";
import {
  MousePointer2, Eraser, Plus, Trash2, Type, Goal, ArrowRight, Minus,
  MoveRight, Activity, PenTool, Square,
  Triangle, CircleDot, Circle, Slash, User, Grid3x3, CircleDashed,
  RectangleHorizontal,
  Undo2, Redo2, Image as ImageIcon, Maximize, Sparkles as SparklesIcon,
} from "lucide-react";
import { COLORS, TOOLS } from "../../lib/tactical/constants";

const ICON_MAP = {
  MousePointer2, Eraser, Type, Goal, ArrowRight, Minus,
  MoveRight, Activity, PenTool, Square,
  Triangle, CircleDot, Circle, Slash, User, Grid3x3, CircleDashed,
  RectangleHorizontal,
};

const CATEGORIES = ["Pedine", "Disegno", "Materiali"];

/**
 * Floating glass-morphism tool palette for the tactical board.
 * Tools are grouped into 3 categories: Pedine (select/erase/text),
 * Disegno (strokes) and Materiali (training equipment).
 */
export default function ToolPalette({
  tool, setTool,
  color, setColor,
  disabled,
  onClearDrawings,
  onClearPlayers,
  onAddPlayer,
  onUndo, onRedo, canUndo, canRedo,
  onExportPng,
  onTogglePresentation,
  isPresentation,
  onApplyTemplate,
  templates = [],
  exporting = false,
}) {
  const byCategory = (cat) => TOOLS.filter((t) => t.category === cat);

  return (
    <div
      className="rounded-2xl border border-slate-200/80 bg-white/70 backdrop-blur-xl shadow-sm p-3 mb-3 flex gap-3 flex-wrap items-center"
      data-testid="tool-palette"
    >
      {/* Tool groups */}
      <div className="flex flex-wrap gap-3 items-center">
        {CATEGORIES.map((cat) => (
          <div key={cat} className="flex flex-col items-start">
            <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold leading-none mb-1 ml-0.5">
              {cat}
            </div>
            <div className="inline-flex gap-1 p-0.5 bg-slate-100/80 rounded-xl flex-wrap">
              {byCategory(cat).map((t) => {
                const Icon = ICON_MAP[t.icon] || MousePointer2;
                const active = tool === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTool(t.id)}
                    title={t.hint}
                    disabled={disabled}
                    data-testid={`tool-${t.id}`}
                    className={`group relative inline-flex items-center justify-center h-8 w-8 rounded-lg transition-all ${
                      active
                        ? "bg-sky-500 text-white shadow-md scale-105"
                        : "text-slate-600 hover:bg-white hover:text-slate-900"
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                  >
                    <Icon size={15} />
                    <span className="absolute -bottom-1 text-[8px] font-mono font-bold opacity-50 group-hover:opacity-100 pointer-events-none">
                      {t.shortcut}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="h-12 w-px bg-slate-200" />

      {/* Color swatches */}
      <div className="flex flex-col items-start">
        <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold leading-none mb-1 ml-0.5">
          Colore
        </div>
        <div className="inline-flex gap-1.5 items-center">
          {COLORS.map((c) => (
            <button
              key={c}
              onClick={() => setColor(c)}
              title={c}
              className={`h-7 w-7 rounded-full border-2 transition-all ${
                color === c ? "border-slate-900 scale-110 shadow" : "border-slate-200 hover:scale-105"
              }`}
              style={{ background: c }}
              data-testid={`color-${c.replace("#", "")}`}
            />
          ))}
        </div>
      </div>

      <div className="h-12 w-px bg-slate-200" />

      {/* Undo/Redo / Templates */}
      <div className="flex flex-col items-start">
        <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold leading-none mb-1 ml-0.5">
          Storia
        </div>
        <div className="inline-flex gap-1">
          <button
            onClick={onUndo}
            disabled={disabled || !canUndo}
            title="Annulla (Ctrl+Z)"
            data-testid="undo-btn"
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg bg-slate-100/80 text-slate-600 hover:bg-white hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Undo2 size={15} />
          </button>
          <button
            onClick={onRedo}
            disabled={disabled || !canRedo}
            title="Ripeti (Ctrl+Y)"
            data-testid="redo-btn"
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg bg-slate-100/80 text-slate-600 hover:bg-white hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Redo2 size={15} />
          </button>
        </div>
      </div>

      {/* Templates dropdown */}
      {templates.length > 0 && (
        <>
          <div className="h-12 w-px bg-slate-200" />
          <div className="flex flex-col items-start">
            <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold leading-none mb-1 ml-0.5">
              Template
            </div>
            <select
              onChange={(e) => onApplyTemplate(e.target.value)}
              value=""
              disabled={disabled}
              className="h-8 px-2 rounded-lg bg-slate-100/80 text-xs font-medium border border-transparent hover:bg-white focus:outline-none focus:ring-2 focus:ring-sky-400 disabled:opacity-40"
              data-testid="templates-select"
            >
              <option value="" disabled>
                Applica modulo…
              </option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      {/* Action buttons */}
      <div className="ml-auto inline-flex flex-wrap gap-1.5">
        <Button size="sm" variant="ghost" onClick={onAddPlayer} disabled={disabled} className="h-9" data-testid="add-player-btn">
          <Plus size={14} className="mr-1" /> Pedina
        </Button>
        <Button size="sm" variant="ghost" onClick={onClearDrawings} disabled={disabled} className="h-9 text-slate-600" data-testid="clear-drawings-btn">
          <Eraser size={14} className="mr-1" /> Disegni
        </Button>
        <Button size="sm" variant="ghost" onClick={onClearPlayers} disabled={disabled} className="h-9 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="clear-players-btn">
          <Trash2 size={14} className="mr-1" /> Pedine
        </Button>
        <Button size="sm" variant="ghost" onClick={onExportPng} disabled={disabled || exporting} className="h-9 text-emerald-700 hover:bg-emerald-50" data-testid="export-png-btn">
          <ImageIcon size={14} className="mr-1" /> {exporting ? "…" : "PNG"}
        </Button>
        <Button
          size="sm"
          variant={isPresentation ? "default" : "ghost"}
          onClick={onTogglePresentation}
          className={`h-9 ${isPresentation ? "bg-amber-500 hover:bg-amber-600 text-white" : "text-amber-700 hover:bg-amber-50"}`}
          data-testid="presentation-toggle"
        >
          {isPresentation ? <SparklesIcon size={14} className="mr-1" /> : <Maximize size={14} className="mr-1" />}
          {isPresentation ? "Esci" : "Presenta"}
        </Button>
      </div>
    </div>
  );
}
